import { Component, Input, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IOsaExecucaoFolhaTesteModel } from '../../../../models/osa-execucao-folha-teste-model';
import { OsaExecucaoFolhaTesteService } from '../../../../services/osa-execucao-folha-teste.service';
import { BaseFormComponent } from './../../../../../app/shared/components/base-form/base-form.component';
import { ToastrService } from 'ngx-toastr';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { OsaExecucaoService } from '../../../../services/osa-execucao.service';
import { OsaExecucaoAbaFolhaTesteIncluirComponent } from './osa-execucao-aba-folha-teste-incluir/osa-execucao-aba-folha-teste-incluir.component';
import { IOsaExecucaoProgramaTrabalhoModel } from '../../../../models/osa-execucao-programa-trabalho.model';
import { OsaExecucaoProgramaTrabalhoService } from '../../../../services/osa-execucao-programa-trabalho.service';
import { IOsaExecucaoModel } from '../../../../models/osa-execucao-model';
import { SharedService } from '../../../../services/shared.service';

@Component({
  selector: 'app-osa-execucao-aba-folha-teste',
  templateUrl: './osa-execucao-aba-folha-teste.component.html',
  styleUrls: ['./osa-execucao-aba-folha-teste.component.scss']
})
export class OsaExecucaoAbaFolhaTesteComponent extends BaseFormComponent implements OnInit, OnDestroy {

  @Input() public id: number = 0;
  @Input() public visualizar: boolean = false;
  @Input() public model: IOsaExecucaoModel;
  public numeroOsa: string = "";
  public planejamentoId: number = 0;
  public listaAgrupadaFolhaTeste: any[] = [];
  public listaProgramaTrabalho: any[] = [];
  public statusProgramaTrabalho: number = 1;
  //-> Crit 21/23: bloqueia ações de PT/folhas com Matriz Preliminar Finalizada/Aprovada/Rejeitada
  public bloqueadoPorMatriz: boolean = false;
  private destroy$ = new Subject<void>();


  constructor(
    route: ActivatedRoute,
    toastr: ToastrService,
    router: Router,
    localeService: BsLocaleService,
    public matDialog: MatDialog,
    private serviceOsaExecucaoFolhaTeste: OsaExecucaoFolhaTesteService,
    private serviceOsaExecucao: OsaExecucaoService,
    private serviceOsaExecucaoProgramaTrabalho: OsaExecucaoProgramaTrabalhoService,
    private sharedService: SharedService,
  ) {
    super(route, toastr, router, localeService, matDialog);
  }

  ngOnInit(): void {
    this.carregarDados();

    //-> Crit 21/23: reflete o status da matriz sem F5 e recarrega PT/folhas ao sincronizar
    this.sharedService.statusMatrizPreliminar$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this.bloqueadoPorMatriz = status === 3 || status === 4 || status === 5;
      });
    this.bloqueadoPorMatriz = [3, 4, 5].includes(this.sharedService.getStatusMatrizPreliminar());

    this.sharedService.getSincronizarDadosEvent()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.carregarDados();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['model'] && this.model) {
      if (this.model?.osa) {
        this.numeroOsa = this.model.osa.numero;
        this.planejamentoId = this.model.osa.planejamentoId;
      }
    }
  }

  carregarDados() {

    this.serviceOsaExecucaoFolhaTeste
      .obterFolhaTestePorExecucao(this.id)
      .then((res) => {
        const lista = res.dados as IOsaExecucaoFolhaTesteModel[];
        this.listaAgrupadaFolhaTeste = this.agruparPorUnidadeGestora(lista);
      })
      .catch((err) => {
        this.exibirMensagemAlerta(err.mensagem.descricao);
      });

    this.serviceOsaExecucaoProgramaTrabalho
      .obterProgramaTrabalhoPorExecucao(this.id)
      .then((res) => {
        const lista = res.dados as IOsaExecucaoProgramaTrabalhoModel[];
        this.listaProgramaTrabalho = lista;
        this.statusProgramaTrabalho = lista[0]?.status;
      })
      .catch((err) => {
        this.exibirMensagemAlerta(err.mensagem.descricao);
      });
  }

  private agruparPorUnidadeGestora(lista: IOsaExecucaoFolhaTesteModel[]): any[] {
    const agrupadoUG: any = {};

    lista.forEach(item => {
      const ugId = item.unidadeGestoraId;
      if (!agrupadoUG[ugId]) {
        agrupadoUG[ugId] = {
          unidadeGestoraId: ugId,
          unidadeGestoraNome: item.unidadeGestora?.nome ?? 'Sem nome',
          amostras: {}
        };
      }

      const amostraId = item.amostraId;
      if (!agrupadoUG[ugId].amostras[amostraId]) {
        agrupadoUG[ugId].amostras[amostraId] = {
          amostraId: amostraId,
          amostraNome: item.amostra?.nome ?? 'Sem nome',
          folhas: []
        };
      }

      agrupadoUG[ugId].amostras[amostraId].folhas.push(item);
    });

    // Converte os objetos em arrays para facilitar o *ngFor
    return Object.values(agrupadoUG).map((ug: any) => ({
      ...ug,
      amostras: Object.values(ug.amostras)
    }));
  }

  public addFolhaTeste(unidadeGestoraId: number, amostraId: number) {
    //-> Crit 21/23: bloqueado com Matriz Preliminar Finalizada/Aprovada/Rejeitada
    if (this.bloqueadoPorMatriz) {
      this.exibirMensagemAlerta('Operação não permitida. Matriz Preliminar Finalizada/Aprovada/Rejeitada — utilize o botão Habilitar PT após rejeição.');
      return;
    }
    const dialogConfig = new MatDialogConfig();
    dialogConfig.id = "modal-folha-teste-incluir";
    dialogConfig.width = "60vw";
    dialogConfig.maxHeight = "60vh";
    dialogConfig.hasBackdrop = true;
    dialogConfig.disableClose = true;
    dialogConfig.data = {
      osaExecucaoId: this.id,
      unidadeGestoraId: unidadeGestoraId,
      amostraId: amostraId,
    };
    const modal = this.matDialog.open(
      OsaExecucaoAbaFolhaTesteIncluirComponent,
      dialogConfig
    );
    modal.afterClosed().subscribe((data) => {
      if (data) {
        this.carregarDados()
      }
    });
  }

  atualizarProgramaTrabalho() {
    this.carregarDados();
  }
}
